import React, { useState, useEffect, useRef } from 'react';
import './NavigationPanel.css';
import ProfileModal from './ProfileModal';

/**
 * Top Navigation Component for LearningANTS
 */
const NavigationPanel = ({ permissions = [], role = '', userData = {} }) => {
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showSettingsDropdown, setShowSettingsDropdown] = useState(false);
  const [showContentDropdown, setShowContentDropdown] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);

  const profileRef = useRef(null);
  
  // Debug log for userData
  useEffect(() => {
    console.log('NavigationPanel mounted with userData:', userData);
  }, [userData]);

  // Use legacy UserUtilities for permission checks
  const hasPermission = (perm) => {
    if (window.UserUtilities) {
      const utils = new window.UserUtilities();
      return utils.hasPermission(perm);
    }
    return permissions.includes(perm);
  };

  // Handle profile actions
  const handleProfileAction = (action) => {
    setShowProfileDropdown(false);
    window.location.hash = action;
  };

  // Handle profile update
  const handleProfileUpdate = (updatedData) => {
    console.log('Profile update requested with:', updatedData);
    // Update the userData state in parent component if needed
    if (typeof window.updateUserData === 'function') {
      window.updateUserData(updatedData);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileDropdown(false);
      }
      if (!event.target.closest('.settings-dropdown')) {
        setShowSettingsDropdown(false);
      }
      if (!event.target.closest('.content-dropdown')) {
        setShowContentDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Check if Settings dropdown should be visible
  const canShowSettingsDropdown = hasPermission('Manage Organization') ||
    hasPermission('Manage Users') ||
    hasPermission('Manage Access Right');

  const canShowContentDropdown = hasPermission('View Syllabus Network') ||
    hasPermission('View Questions') ||
    hasPermission('Create Scaffolds') ||
    hasPermission('Approval Scaffolds');

  // Early return if no userData
  if (!userData) {
    console.error('NavigationPanel: userData is undefined or null');
    return null;
  }

  return (
    <>
      <nav className="navbar navbar-expand-lg navbar-dark bg-dark fixed-top">
        <div className="container-fluid">
          {/* Brand */}
          <a className="navbar-brand" href="#Dashboard">
            <img 
              src='https://d6adgef862n1y.cloudfront.net/learningANTS_logo_white2.png'
              alt="LearningANTS"
              height="30"
            />
          </a>
          {/* Mobile Toggle */}
          <button 
            className="navbar-toggler" 
            type="button" 
            data-bs-toggle="collapse" 
            data-bs-target="#navbarContent"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          {/* Collapsible Nav and Profile */}
          <div className="collapse navbar-collapse" id="navbarContent">
            <ul className="navbar-nav ms-auto mb-2 mb-lg-0">
              {/* Dashboard */}
              <li className="nav-item">
                <a className="nav-link" href="#Dashboard">
                  <i className="fas fa-home me-1"></i>
                  Dashboard
                </a>
              </li>
              {/* Settings Dropdown */}
              {canShowSettingsDropdown && (
                <li className="nav-item dropdown settings-dropdown">
                  <button
                    className="nav-link dropdown-toggle"
                    onClick={() => setShowSettingsDropdown(!showSettingsDropdown)}
                  >
                    <i className="fas fa-cog me-1"></i>
                    Settings
                  </button>
                  {showSettingsDropdown && (
                    <ul className="dropdown-menu show">
                      {hasPermission('Manage Organization') && (
                        <li>
                          <a className="dropdown-item" href="#Organizations">
                            <i className="fas fa-building me-2"></i>
                            Organization
                          </a>
                        </li>
                      )}
                      {hasPermission('Manage Users') && (
                        <li>
                          <a className="dropdown-item" href="#Users">
                            <i className="fas fa-users me-2"></i>
                            Users
                          </a>
                        </li>
                      )}
                      {hasPermission('Manage Access Right') && (
                        <li>
                          <a className="dropdown-item" href="#Permissions">
                            <i className="fas fa-lock me-2"></i>
                            Permissions
                          </a>
                        </li>
                      )}
                    </ul>
                  )}
                </li>
              )}
              {/* Content Dropdown */}
              {canShowContentDropdown && (
                <li className="nav-item dropdown content-dropdown">
                  <button
                    className="nav-link dropdown-toggle"
                    onClick={() => setShowContentDropdown(!showContentDropdown)}
                  >
                    <i className="fas fa-book me-1"></i>
                    Content
                  </button>
                  {showContentDropdown && (
                    <ul className="dropdown-menu show">
                      {hasPermission('View Syllabus Network') && (
                        <li>
                          <a className="dropdown-item" href="#Network">
                            <i className="fas fa-network-wired me-2"></i>
                            Network
                          </a>
                        </li>
                      )}
                      {hasPermission('View Questions') && (
                        <li>
                          <a className="dropdown-item" href="#Questions">
                            <i className="fas fa-question-circle me-2"></i>
                            Questions
                          </a>
                        </li>
                      )}
                      {hasPermission('Create Scaffolds') && (
                        <li>
                          <a className="dropdown-item" href="#MyScaffolds">
                            <i className="fas fa-layer-group me-2"></i>
                            Scaffolds
                          </a>
                        </li>
                      )}
                      {hasPermission('Approval Scaffolds') && (
                        <li>
                          <a className="dropdown-item" href="#ApproveScaffolds">
                            <i className="fas fa-check-circle me-2"></i>
                            Approve Scaffolds
                          </a>
                        </li>
                      )}
                    </ul>
                  )}
                </li>
              )}
              {/* Module Admin */}
              {hasPermission('Manage Module') && (
                <li className="nav-item">
                  <a className="nav-link" href="#ModuleAdmin">
                    <i className="fas fa-cube me-1"></i>
                    Module Admin
                  </a>
                </li>
              )}
              {/* Profile Section */}
              <li className="nav-item me-5" ref={profileRef}>
                <button
                  className="btn btn-link nav-link dropdown-toggle d-flex align-items-center"
                  onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                  style={{ minWidth: 0 }}
                  aria-label="User menu"
                >
                  <img
                    src={userData.profileimage && userData.profileimage.trim() !== '' ? `https://d6adgef862n1y.cloudfront.net/${userData.profileimage}` : 'css/images/loginman.png'}
                    alt="Profile"
                    className="rounded-circle me-2"
                    width="32"
                    height="32"
                    onError={e => {
                      if (!e.target.src.endsWith('default_profile.png')) {
                        e.target.onerror = null; // Prevent infinite loop
                        e.target.src = 'css/images/default_profile.png';
                      }
                    }}
                  />
                  <span>{userData.firstname || 'Guest'}</span>
                </button>
                {showProfileDropdown && (
                  <ul className="dropdown-menu show">
                    <li>
                      <a 
                        className="dropdown-item" 
                        href="#" 
                        onClick={(e) => {
                          e.preventDefault();
                          setShowProfileModal(true);
                          setShowProfileDropdown(false);
                        }}
                      >
                        <i className="fas fa-user me-2"></i>
                        Update Profile
                      </a>
                    </li>
                    <li>
                      <a 
                        className="dropdown-item" 
                        href="#" 
                        onClick={(e) => {
                          e.preventDefault();
                          if (window.ShowChangePasswordDialog) {
                            window.ShowChangePasswordDialog();
                          }
                          setShowProfileDropdown(false);
                        }}
                      >
                        <i className="fas fa-key me-2"></i>
                        Change Password
                      </a>
                    </li>
                    <li>
                      <a 
                        className="dropdown-item" 
                        href="#Logout" 
                        onClick={() => {
                          setShowProfileDropdown(false);
                          window.location.hash = 'Logout';
                        }}
                      >
                        <i className="fas fa-sign-out-alt me-2"></i>
                        Logout
                      </a>
                    </li>
                  </ul>
                )}
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Profile Modal */}
      <ProfileModal
        show={showProfileModal}
        onHide={() => setShowProfileModal(false)}
        userData={userData}
        onUpdateProfile={handleProfileUpdate}
      />
    </>
  );
};

export default NavigationPanel; 